import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenBreastCancerProceduresIndicatorC7Data } from "@/services/esus/indicator/esf/c7/getDetailedCitizensBreastCancerProceduresIndicatorC7";
import { getCitizenScoreStatusDescriptionIndicatorC7 } from "@/utils/indicatorsEsf";

import { IndicatorEsfC7CitizensProceduresFilters } from "./IndicatorEsfC7CitizensProcedureFilters";
import { IndicatorEsfC7CitizensProceduresTable } from "./IndicatorEsfC7CitizensProceduresTable";
import { IndicatorEsfC7CitizenProceduresHistory } from "./IndicatorEsfC7CitizenProceduresHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC7CitizensBreastCancerProceduresProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedProcedures: Paginated<DetailedCitizenBreastCancerProceduresIndicatorC7Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedIndicatorC7: () => Promise<
		DetailedCitizenBreastCancerProceduresIndicatorC7Data[]
	>;
};

export function IndicatorEsfC7CitizensBreastCancerProcedures({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedProcedures,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedIndicatorC7: handleGetAllDetailedIndicatorC1,
}: Readonly<IndicatorEsfC7CitizensBreastCancerProceduresProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithProcedures, setCitizenWithProcedures] =
		useState<DetailedCitizenBreastCancerProceduresIndicatorC7Data>(
			new DetailedCitizenBreastCancerProceduresIndicatorC7Data()
		);
	const [isLoadingCitizenWithProcedures, setIsLoadingCitizenWithProcedures] = useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithProcedures(
		citizenWithProcedures: DetailedCitizenBreastCancerProceduresIndicatorC7Data
	) {
		setIsLoadingCitizenWithProcedures(true);
		setCitizenWithProcedures(citizenWithProcedures);
		setIsLoadingCitizenWithProcedures(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorC1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorProceduresToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Prática D - Exames Câncer de Mama - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorProceduresToGenericObjectArray(
		citizensWithProcedures: DetailedCitizenBreastCancerProceduresIndicatorC7Data[]
	) {
		return citizensWithProcedures.map(
			(citizenWithProcedures) =>
				({
					Nome: citizenWithProcedures.fullName,
					DN: citizenWithProcedures.birthdate
						? dayjs(citizenWithProcedures.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: citizenWithProcedures.birthdate
						? getAge(citizenWithProcedures.birthdate)
						: "",
					Documento: citizenWithProcedures.cpf ?? citizenWithProcedures.cns ?? "",
					Area: citizenWithProcedures.area ?? "FA",
					"Ine Equipe": citizenWithProcedures.healthTeam?.ine ?? "",
					"Nome Equipe": citizenWithProcedures.healthTeam?.name ?? "",
					"Último exame": citizenWithProcedures.attendanceProcedureReqEvals?.at(0)?.date
						? dayjs(
								citizenWithProcedures.attendanceProcedureReqEvals.at(0)?.date
						  ).format("DD/MM/YYYY")
						: "",
					"Status último exame": getCitizenScoreStatusDescriptionIndicatorC7(
						citizenWithProcedures.lastBreastCancerProcedureStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithProcedures || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC7CitizensProceduresFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC7CitizensProceduresTable
							citizensWithProcedures={paginatedProcedures.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithProcedures}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedProcedures?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedProcedures.pageSize}
								totalPages={paginatedProcedures.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Exames/Procedimentos
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC7CitizenProceduresHistory
							citizenWithProcedures={citizenWithProcedures}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
